import Tooltip from '@mortgage-pos/ui/components/Tooltip'
import { Routes } from '@mortgage-pos/types'
import { useNavigate } from 'react-router-dom'
import React, { FC, useEffect, useState } from 'react'
import { FEATURE_FLAG_TYPES } from '@mortgage-pos/data'
import newRelic from '@mortgage-pos/ui/services/newRelic'
import DownArrow from '@mortgage-pos/ui/assets/arrow-down.svg'
import ShieldIcon from '@mortgage-pos/ui/assets/ShieldIcon.svg'
import InfoBlock from '@mortgage-pos/ui/components/InfoBlock'
import { useStoreActions, useStoreState } from '@mortgage-pos/ui/store'
import { useVariation } from '@mortgage-pos/ui/services/featureFlags'
import FileUpload from '@mortgage-pos/ui/components/compare/FileUpload'
import { supportedFileFormats } from '@mortgage-pos/ui/data/supportedFileFormats'
import LoadingWithValueProps from '@mortgage-pos/ui/components/LoadingWithValueProps'

import {
  customTextMap,
  iconMap,
} from '@mortgage-pos/ui/utils/customizationHelpers'

interface UploaderProps {
  topSubHeader?: string
  title?: string
  subTitle?: string
  uploadButtonText?: string
  uploadErrorText?: string
  documentType?: string
}

const Uploader: FC<UploaderProps> = ({
  topSubHeader = '',
  title = '',
  subTitle = '',
  uploadButtonText = '',
  uploadErrorText = '',
  documentType = 'loanEstimate',
}) => {
  const navigate = useNavigate()

  const [uploadAttemptCount, setUploadAttemptCount] = useState<number>(0)
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false)

  const [showFormatErrorMessage, setShowFormatErrorMessage] =
    useState<boolean>(false)

  const { isLeComparisonRunning } = useStoreState((state) => state.leComparison)

  const { extractLEDetails, setIsLeComparisonRunning, setLeComparisonApp } =
    useStoreActions((state) => state.leComparison)

  const { next } = useStoreActions((store) => store.questionnaire)
  const { elementClicked } = useStoreActions((store) => store.tracking)

  const showCompareDemo = useVariation(
    FEATURE_FLAG_TYPES.ENABLE_COMPARE_LE_DEMO
  )

  const isCompareLightningTest = useVariation(
    FEATURE_FLAG_TYPES.COMPARE_LIGHTNING_TEST_ENABLED
  )

  useEffect(() => {
    if (typeof showCompareDemo === 'boolean' && !showCompareDemo) {
      navigate(`/${Routes.APP_1003}`)
    }
  }, [navigate, showCompareDemo])

  useEffect(() => {
    newRelic.increment('compare_page_viewed')
  }, [])

  useEffect(() => {
    setLeComparisonApp(true)
  }, [])

  const handleFileUpload = async (file: File) => {
    setIsLeComparisonRunning(true)
    setShowErrorMessage(false)
    setShowFormatErrorMessage(false)

    elementClicked({
      elementType: 'Button',
      location: 'Compare Upload page',
      actionOutcome: 'Uploads a document',
      text: uploadButtonText,
    })

    const fileFormat = file.type ? file.type.split('/')[1] : ''

    if (!supportedFileFormats.includes(fileFormat.toLowerCase())) {
      setShowFormatErrorMessage(true)
      setIsLeComparisonRunning(false)
      return
    }

    let results

    try {
      setUploadAttemptCount(uploadAttemptCount + 1)
      results = await extractLEDetails({ file, documentType })
    } catch (e) {
      if (uploadAttemptCount > 2) {
        // Allow to proceed - compare flow uses visibleIfs to display extra page for manual LE data entry
        newRelic.increment('upload_loan_estimate.fail_forward')
        await next()
      }

      setShowErrorMessage(true)
      setIsLeComparisonRunning(false)
      return
    }

    // For POC purposes only
    if (new URLSearchParams(window.location.search).get('br-redirect')) {
      const bankrateBaseUrl =
        'https://www.bankrate.com/mortgages/mortgage-rates/?partnerId=br3&pid=br3&pointsChanged=false&searchChanged=false&disablePre=1&'
      const bankrateUrlParams = new URLSearchParams()
      const { leData, answersFromLe } = results
      bankrateUrlParams.append('refinanceCashOutAmount', leData.cashOut)
      bankrateUrlParams.append('refinanceLoanAmount', leData.loanAmount)
      bankrateUrlParams.append('refinanceLoanTerms', leData.term + 'yr')
      bankrateUrlParams.append('refinancePropertyType', 'SingleFamily')
      bankrateUrlParams.append('PrimaryResidence', 'PrimaryResidence')
      bankrateUrlParams.append('refinancePropertyValue', leData.propertyValue)
      bankrateUrlParams.append('zipCode', answersFromLe.zipCode)

      window.location.href = bankrateBaseUrl + bankrateUrlParams.toString()
    } else {
      await next()
    }
  }

  const tooltipContent = {
    title: 'What we assume',
    text: `In addition to the details shown and extracted from your document,
      we use public information to identify mortgage insurance premiums,
      property taxes, property insurance and the interest rate for your
      property. If public information is not available, we use standard values
      in our calculations. Results do not account for all variables of your
      specific loan transaction, and provide a preliminary estimate for
      comparison purposes only. Insurance and taxes are not included in these
      estimates. This is NOT a mortgage loan approval or commitment to lend,
      and all estimates are subject to change without notice. Terms and
      conditions apply.`,
  }

  const monthlyPaymentTooltipContent = {
    text: `The average monthly payment savings estimate is based on Sage
      borrowers who refinanced with the same loan term on their previous
      mortgage between January 2021 and May 2021. The savings calculation is
      derived by taking the monthly principal and interest payment of the
      borrower's previous mortgage and subtracting the monthly and interest
      payment on their new mortgage refinanced by Sage. Individual savings
      could vary based on current market rates, property type, loan amount,
      loan to value, debt to income ratio, credit score, and other variables.`,
  }

  const uploadWrapperModClass = isCompareLightningTest ? '--truncated' : ''

  return (
    <>
      <div className="compare-page__heading-group">
        <p className="compare-page__subtitle">{topSubHeader}</p>

        <h1 className="compare-page__title">{title}</h1>

        {isLeComparisonRunning ? null : (
          <p className="compare-page__description">
            {subTitle}

            {isCompareLightningTest ? (
              <Tooltip
                className="compare-page__legal-tooltip"
                title={tooltipContent.title}
                text={tooltipContent.text}
              />
            ) : null}
          </p>
        )}
      </div>

      <div className={`compare-page__upload-wrapper ${uploadWrapperModClass}`}>
        {showErrorMessage ? (
          <InfoBlock type="danger">
            {uploadErrorText ||
              'Oops! We failed to to read your file. Please try again.'}
          </InfoBlock>
        ) : null}

        {showFormatErrorMessage ? (
          <InfoBlock type="danger">
            Please try uploading one of the supported formats: PDF, TIFF, JPG,
            PNG, HEIC, HEIF.
          </InfoBlock>
        ) : null}

        {isLeComparisonRunning ? (
          <LoadingWithValueProps />
        ) : (
          <FileUpload
            fileUploadHandler={handleFileUpload}
            uploadButtonText={uploadButtonText}
          />
        )}

        {isCompareLightningTest && !isLeComparisonRunning ? (
          <button
            className="compare-page__next link"
            onClick={() => {
              newRelic.increment('compare_page.click_manual_address_entry')
              next()
            }}
          >
            Or enter your address manually
            <img
              className="compare-page__next-arrow"
              src={DownArrow}
              alt="Arrow down icon"
            />
          </button>
        ) : null}
      </div>

      {isLeComparisonRunning && isCompareLightningTest ? null : (
        <div className="compare-security-msg__wrap">
          <div className="compare-security-msg">
            <div className="compare-security-msg__blurb">
              <img
                className="compare-security-msg__shield-icon"
                src={ShieldIcon}
                alt="Shield"
              />
              Your data is securely encrypted
            </div>

            <div className="compare-security-msg__disclosure">
              <p>
                The information in your mortgage statement allows us to gather
                data from third parties (property value estimator) to pre-fill
                information on your behalf and to display preliminary estimated
                offer comparisons for your convenience. With Sage, all consumer
                data is encrypted and is maintained with industry-standard
                security measures.
              </p>
            </div>
          </div>
        </div>
      )}

      {isLeComparisonRunning ? null : (
        <div className="compare-page__values">
          <div className="compare-page__value">
            <img
              className="compare-page__value-img"
              src={iconMap('money')}
              alt="Icon of money saved"
            />

            <div className="compare-page__text-container">
              <h4 className="compare-page__value-subtitle">
                {customTextMap('valueProp1').title}

                <Tooltip
                  className="compare-page__legal-tooltip"
                  text={monthlyPaymentTooltipContent.text}
                />
              </h4>

              <p>{customTextMap('valueProp1').description}</p>
            </div>
          </div>

          <div className="compare-page__value">
            <img
              className="compare-page__value-img"
              src={iconMap('time')}
              alt="Icon of time saved"
            />

            <div className="compare-page__text-container">
              <h4 className="compare-page__value-subtitle">
                {customTextMap('valueProp2').title}
              </h4>

              <p>{customTextMap('valueProp2').description}</p>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default Uploader
